import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Modal,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Team, Profile } from "@/types";

/**
 * Extended team with member count
 */
interface TeamWithMembers extends Team {
  member_count: number;
  members: Pick<Profile, "id" | "full_name" | "avatar_url">[];
}

/**
 * Team card component
 */
interface TeamCardProps {
  team: TeamWithMembers;
  onPress: () => void;
  onManageMembers: () => void;
}

const TeamCard = ({ team, onPress, onManageMembers }: TeamCardProps) => {
  const colors = useThemeColors();
  const teamColor = team.color || colors.primary;

  return (
    <Pressable
      onPress={onPress}
      className="rounded-xl overflow-hidden mb-4 active:opacity-90"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Team ${team.name}`}
    >
      {/* Color bar */}
      <View className="h-2" style={{ backgroundColor: teamColor }} />

      <View className="p-4">
        {/* Header */}
        <View className="flex-row items-center justify-between mb-3">
          <View className="flex-row items-center flex-1">
            <View
              className="w-12 h-12 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: `${teamColor}20` }}
            >
              {team.avatar_url ? (
                <Text className="text-2xl">🎨</Text>
              ) : (
                <Text className="text-xl font-bold" style={{ color: teamColor }}>
                  {team.name.charAt(0).toUpperCase()}
                </Text>
              )}
            </View>
            <View className="flex-1">
              <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                {team.name}
              </Text>
              {team.description && (
                <Text
                  className="text-sm italic"
                  numberOfLines={1}
                  style={{ color: colors.textSecondary }}
                >
                  "{team.description}"
                </Text>
              )}
            </View>
          </View>
          <View className="items-end">
            <Text className="text-xl font-bold" style={{ color: teamColor }}>
              {team.total_score}
            </Text>
            <Text className="text-xs" style={{ color: colors.textTertiary }}>
              punten
            </Text>
          </View>
        </View>

        {/* Members preview */}
        <View className="flex-row items-center mb-3">
          <View className="flex-row">
            {team.members.slice(0, 5).map((member, index) => (
              <View
                key={member.id}
                className="w-8 h-8 rounded-full items-center justify-center border-2"
                style={{
                  backgroundColor: colors.border,
                  borderColor: colors.surface,
                  marginLeft: index > 0 ? -8 : 0,
                }}
              >
                <Text className="text-xs font-medium" style={{ color: colors.textSecondary }}>
                  {member.full_name?.charAt(0).toUpperCase() || "?"}
                </Text>
              </View>
            ))}
          </View>
          <Text className="text-sm ml-2" style={{ color: colors.textSecondary }}>
            {team.member_count} {team.member_count === 1 ? "lid" : "leden"}
          </Text>
        </View>

        {/* Actions */}
        <View className="flex-row">
          <Pressable
            onPress={onPress}
            className="flex-1 rounded-lg py-2 mr-2 flex-row items-center justify-center active:opacity-80"
            style={{ backgroundColor: "#FEF3C7" }}
            accessibilityRole="button"
            accessibilityLabel="Team bewerken"
          >
            <Ionicons name="pencil-outline" size={16} color="#D97706" />
            <Text className="font-medium ml-1" style={{ color: "#B45309" }}>
              Bewerken
            </Text>
          </Pressable>
          <Pressable
            onPress={onManageMembers}
            className="flex-1 rounded-lg py-2 ml-2 flex-row items-center justify-center active:opacity-80"
            style={{ backgroundColor: "#FEF3C7" }}
            accessibilityRole="button"
            accessibilityLabel="Leden beheren"
          >
            <Ionicons name="people-outline" size={16} color="#D97706" />
            <Text className="font-medium ml-1" style={{ color: "#B45309" }}>
              Leden
            </Text>
          </Pressable>
        </View>
      </View>
    </Pressable>
  );
};

/**
 * Teams Management Screen
 */
export default function TeamsManagementScreen() {
  const router = useRouter();
  const { activeWeekend, user } = useAuth();
  const colors = useThemeColors();

  const [teams, setTeams] = useState<TeamWithMembers[]>([]);
  const [unassignedUsers, setUnassignedUsers] = useState<Pick<Profile, "id" | "full_name" | "avatar_url" | "email">[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showShuffleModal, setShowShuffleModal] = useState(false);
  const [includeAdminsInShuffle, setIncludeAdminsInShuffle] = useState(false);

  /**
   * Fetch teams and unassigned users
   */
  const fetchData = useCallback(async () => {
    if (!activeWeekend) return;

    try {
      // Fetch teams with members
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select(`
          *,
          team_members (
            user_id,
            profiles:user_id (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .eq("weekend_id", activeWeekend.id)
        .order("name");

      if (teamsError) throw teamsError;

      // Transform data
      const transformedTeams: TeamWithMembers[] = (teamsData || []).map((team: any) => ({
        ...team,
        member_count: team.team_members?.length || 0,
        members: team.team_members?.map((tm: any) => tm.profiles).filter(Boolean) || [],
      }));

      setTeams(transformedTeams);

      // Get all users assigned to teams for this weekend
      const assignedUserIds = transformedTeams.flatMap((t) => t.members.map((m) => m.id));

      // Fetch all profiles
      const { data: allProfiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, email")
        .order("full_name");

      if (profilesError) throw profilesError;

      // Filter unassigned users
      const unassigned = (allProfiles || []).filter(
        (profile) => !assignedUserIds.includes(profile.id)
      );

      setUnassignedUsers(unassigned);
    } catch (error) {
      console.error("Error fetching teams data:", error);
      Alert.alert("Fout", "Kon teams niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeWeekend]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  /**
   * Handle create team
   */
  const handleCreateTeam = () => {
    router.push("/(admin)/teams/create" as any);
  };

  /**
   * Handle edit team
   */
  const handleEditTeam = (team: TeamWithMembers) => {
    router.push(`/(admin)/teams/${team.id}` as any);
  };

  /**
   * Handle manage members
   */
  const handleManageMembers = (team: TeamWithMembers) => {
    router.push(`/(admin)/teams/members/${team.id}` as any);
  };

  /**
   * Shuffle users into teams
   */
  const handleShuffle = async () => {
    if (!activeWeekend || !user) return;
    if (teams.length === 0) {
      Alert.alert("Geen teams", "Maak eerst teams aan voordat je gebruikers kunt verdelen.");
      return;
    }

    setIsShuffling(true);

    try {
      // Get users to shuffle
      let usersToShuffle = [...unassignedUsers];

      // Optionally include admins
      if (!includeAdminsInShuffle) {
        // Get admin user IDs
        const { data: adminRoles } = await supabase
          .from("user_roles")
          .select("user_id")
          .in("role", ["super_admin", "familie_admin"])
          .is("revoked_at", null);

        const adminIds = adminRoles?.map((r) => r.user_id) || [];
        usersToShuffle = usersToShuffle.filter((u) => !adminIds.includes(u.id));
      }

      if (usersToShuffle.length === 0) {
        Alert.alert("Geen gebruikers", "Er zijn geen gebruikers om te verdelen.");
        setIsShuffling(false);
        setShowShuffleModal(false);
        return;
      }

      // Shuffle array
      const shuffled = usersToShuffle.sort(() => Math.random() - 0.5);

      // Distribute evenly across teams
      const teamAssignments: { team_id: string; user_id: string; added_by: string }[] = [];

      shuffled.forEach((userProfile, index) => {
        const teamIndex = index % teams.length;
        teamAssignments.push({
          team_id: teams[teamIndex].id,
          user_id: userProfile.id,
          added_by: user.id,
        });
      });

      // Insert all assignments
      const { error } = await supabase.from("team_members").insert(teamAssignments);

      if (error) throw error;

      Alert.alert(
        "Succes! 🎲",
        `${shuffled.length} gebruikers zijn verdeeld over ${teams.length} teams.`
      );

      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error shuffling users:", error);
      Alert.alert("Fout", "Kon gebruikers niet verdelen");
    } finally {
      setIsShuffling(false);
      setShowShuffleModal(false);
    }
  };

  if (!activeWeekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="calendar-outline" size={48} color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Geen actief weekend
        </Text>
      </SafeAreaView>
    );
  }

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Teams laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Action Bar */}
      <View className="px-4 py-3 flex-row items-center justify-between">
        <Pressable
          onPress={handleCreateTeam}
          className="flex-1 rounded-xl py-3 mr-2 flex-row items-center justify-center active:opacity-80"
          style={{ backgroundColor: "#F59E0B" }}
          accessibilityRole="button"
          accessibilityLabel="Nieuw team maken"
        >
          <Ionicons name="add-circle-outline" size={20} color="#FFFFFF" />
          <Text className="text-white font-semibold ml-2">Nieuw Team</Text>
        </Pressable>

        {unassignedUsers.length > 0 && teams.length > 0 && (
          <Pressable
            onPress={() => setShowShuffleModal(true)}
            className="rounded-xl py-3 px-4 flex-row items-center justify-center active:opacity-80"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: "#FCD34D",
            }}
            accessibilityRole="button"
            accessibilityLabel="Gebruikers shuffelen"
          >
            <Ionicons name="shuffle-outline" size={20} color="#D97706" />
          </Pressable>
        )}
      </View>

      {/* Stats Bar */}
      <View className="px-4 pb-3 flex-row">
        <View
          className="rounded-lg px-3 py-2 mr-2 flex-row items-center"
          style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
        >
          <Ionicons name="people" size={16} color="#D97706" />
          <Text className="font-medium ml-1" style={{ color: "#D97706" }}>
            {teams.length} teams
          </Text>
        </View>
        {unassignedUsers.length > 0 && (
          <View
            className="rounded-lg px-3 py-2 flex-row items-center"
            style={{ backgroundColor: "#FEE2E2" }}
          >
            <Ionicons name="alert-circle" size={16} color="#EF4444" />
            <Text className="font-medium ml-1" style={{ color: "#DC2626" }}>
              {unassignedUsers.length} zonder team
            </Text>
          </View>
        )}
      </View>

      <ScrollView
        className="flex-1 px-4"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor="#D97706"
          />
        }
      >
        {/* Teams List */}
        {teams.length > 0 ? (
          teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              onPress={() => handleEditTeam(team)}
              onManageMembers={() => handleManageMembers(team)}
            />
          ))
        ) : (
          <View
            className="rounded-xl p-8 items-center"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Ionicons name="people-outline" size={48} color={colors.border} />
            <Text className="mt-2 text-center" style={{ color: colors.textTertiary }}>
              Nog geen teams
            </Text>
            <Text className="text-sm text-center mt-1" style={{ color: colors.textTertiary }}>
              Maak teams aan om te beginnen
            </Text>
          </View>
        )}

        {/* Unassigned Users Section */}
        {unassignedUsers.length > 0 && (
          <View className="mt-6 mb-8">
            <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: "#D97706" }}>
              Zonder Team ({unassignedUsers.length})
            </Text>
            <View
              className="rounded-xl overflow-hidden"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              {unassignedUsers.slice(0, 10).map((profile, index) => (
                <View
                  key={profile.id}
                  className="flex-row items-center px-4 py-3"
                  style={{
                    borderBottomWidth: index < Math.min(unassignedUsers.length - 1, 9) ? 1 : 0,
                    borderBottomColor: colors.border,
                  }}
                >
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: colors.border }}
                  >
                    <Text className="font-medium" style={{ color: colors.textSecondary }}>
                      {profile.full_name?.charAt(0).toUpperCase() || "?"}
                    </Text>
                  </View>
                  <Text className="flex-1" style={{ color: colors.text }}>
                    {profile.full_name || "Onbekend"}
                  </Text>
                  <Ionicons name="alert-circle-outline" size={20} color="#EF4444" />
                </View>
              ))}
              {unassignedUsers.length > 10 && (
                <View className="px-4 py-3" style={{ backgroundColor: colors.background }}>
                  <Text className="text-center" style={{ color: colors.textSecondary }}>
                    +{unassignedUsers.length - 10} meer...
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        <View className="h-4" />
      </ScrollView>

      {/* Shuffle Modal */}
      <Modal visible={showShuffleModal} transparent animationType="fade">
        <View className="flex-1 bg-black/50 items-center justify-center px-4">
          <View
            className="rounded-2xl w-full max-w-sm overflow-hidden"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="p-6">
              <View className="items-center mb-4">
                <View className="w-16 h-16 rounded-full bg-amber-100 items-center justify-center mb-3">
                  <Ionicons name="shuffle" size={32} color="#D97706" />
                </View>
                <Text className="text-xl font-bold" style={{ color: colors.text }}>
                  Teams Shuffelen
                </Text>
              </View>

              <Text className="text-center mb-4" style={{ color: colors.textSecondary }}>
                {unassignedUsers.length} gebruikers worden willekeurig verdeeld over{" "}
                {teams.length} teams.
              </Text>

              {/* Options */}
              <Pressable
                onPress={() => setIncludeAdminsInShuffle(!includeAdminsInShuffle)}
                className="flex-row items-center rounded-xl p-4 mb-4"
                style={{ backgroundColor: colors.background }}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: includeAdminsInShuffle }}
              >
                <View
                  className="w-6 h-6 rounded-md border-2 items-center justify-center mr-3"
                  style={{
                    backgroundColor: includeAdminsInShuffle ? "#F59E0B" : "transparent",
                    borderColor: includeAdminsInShuffle ? "#F59E0B" : colors.border,
                  }}
                >
                  {includeAdminsInShuffle && (
                    <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                  )}
                </View>
                <View className="flex-1">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    Familie admins meenemen
                  </Text>
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    Admins worden ook in teams geplaatst
                  </Text>
                </View>
              </Pressable>

              {/* Actions */}
              <View className="flex-row">
                <Pressable
                  onPress={() => setShowShuffleModal(false)}
                  className="flex-1 py-3 mr-2 items-center rounded-xl active:opacity-80"
                  style={{ backgroundColor: colors.border }}
                  disabled={isShuffling}
                  accessibilityRole="button"
                  accessibilityLabel="Annuleren"
                >
                  <Text className="font-semibold" style={{ color: colors.textSecondary }}>
                    Annuleer
                  </Text>
                </Pressable>
                <Pressable
                  onPress={handleShuffle}
                  className="flex-1 py-3 ml-2 items-center rounded-xl active:opacity-80"
                  style={{ backgroundColor: "#F59E0B" }}
                  disabled={isShuffling}
                  accessibilityRole="button"
                  accessibilityLabel="Shuffle starten"
                >
                  {isShuffling ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text className="font-semibold text-white">🎲 Shuffle!</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}
